import React, { useMemo, useState } from "react";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { TableLoader, TableNotFound } from "src/Utils/TableLoader";
import type { ISessionRequest } from "src/Types/module/b2bSession/b2bSessionRequest.types";
import CreateSessionRequestModal from "./CreateSessionRequestModal";
import SessionRequestTableRow from "./SessionRequestTableRow";

const SessionRequestList: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
	const [pagination, setPagination] = useState({
		limit: 50,
		skip: 0,
	});

	const { data, isLoading } = useGetQueryWithTotal<ISessionRequest[]>({
		endPoint: "/api/fair/admin/sessions/requests",
		queryKeys: ["SessionRequests", "SessionRequest"],
	});

	const requestList: ISessionRequest[] = useMemo(() => {
		if (Array.isArray(data?.data)) return data.data;
		if (Array.isArray(data)) return (data as unknown) as ISessionRequest[];
		return [];
	}, [data]);

	// Filter by search query and status
	const filteredRequests = useMemo(() => {
		let list = requestList;

		if (statusFilter && statusFilter !== "all") {
			list = list.filter(
				(item) => item.status?.toLowerCase() === statusFilter.toLowerCase()
			);
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			list = list.filter((item) => {
				const fromName = `${item.from_b2b_first_name || ""} ${item.from_b2b_last_name || ""
					}`.toLowerCase();
				const toName = `${item.to_b2b_first_name || ""} ${item.to_b2b_last_name || ""
					}`.toLowerCase();
				const fromOrg = (item.from_b2b_organization || "").toLowerCase();
				const toOrg = (item.to_b2b_organization || "").toLowerCase();
				const fromEmail = (item.from_b2b_email || "").toLowerCase();
				const toEmail = (item.to_b2b_email || "").toLowerCase();
				const sessionNo = (item.session_no || "").toLowerCase();
				const note = (item.note || "").toLowerCase();
				const date = (item.date || "").toLowerCase();

				return (
					fromName.includes(query) ||
					toName.includes(query) ||
					fromOrg.includes(query) ||
					toOrg.includes(query) ||
					fromEmail.includes(query) ||
					toEmail.includes(query) ||
					sessionNo.includes(query) ||
					note.includes(query) ||
					date.includes(query) ||
					String(item.id).includes(query)
				);
			});
		}

		return list;
	}, [requestList, statusFilter, searchQuery]);

	// Paginated slice
	const paginatedData = useMemo(() => {
		return filteredRequests.slice(
			pagination.skip,
			pagination.skip + pagination.limit
		);
	}, [filteredRequests, pagination.skip, pagination.limit]);

	return (
		<CommCard
			showCardHeader
			headerTitle={`Session Request List (${filteredRequests.length})`}
			helmetTitle="BTTF - B2B Session Request List"
			showSearch
			searchPlaceHolder="Search by member name, organization, session..."
			handleSearchFun={(query) => {
				setSearchQuery(query);
				setPagination((prev) => ({ ...prev, skip: 0 }));
			}}
			showStatus
			statusLabel="Status"
			statusOptions={[
				{ id: "all", name: "All" },
				{ id: "pending", name: "Pending" },
				{ id: "approved", name: "Approved" },
				{ id: "rejected", name: "Rejected" },
				{ id: "completed", name: "Completed" },
			]}
			handleSelectStatus={(selectedStatus) => {
				setStatusFilter(selectedStatus);
				setPagination((prev) => ({ ...prev, skip: 0 }));
			}}
			showButton
			btnText="Create Request"
			handleBtnForModalFun={() => setOpenCreateModal(true)}
		>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>SL.</TableCell>
							<TableCell>From B2B (Requester)</TableCell>
							<TableCell>To B2B (Invited)</TableCell>
							<TableCell>Session Slot</TableCell>
							<TableCell>Note / Reason</TableCell>
							<TableCell>Status</TableCell>

							<TableCell align="right">Action</TableCell>
						</TableRow>
					</TableHead>

					{isLoading ? (
						<TableLoader />
					) : paginatedData.length > 0 ? (
						<TableBody>
							{paginatedData.map((request, index) => (
								<SessionRequestTableRow
									key={request.id}
									request={request}
									serial={pagination.skip + index + 1}
								/>
							))}
						</TableBody>
					) : (
						<TableNotFound />
					)}
				</Table>
			</TableContainer>

			{filteredRequests.length > 0 && (
				<Box p={2}>
					<CommonPagination
						total={filteredRequests.length}
						dataShowPerPage={pagination.limit}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>
			)}

			{/* Create Session Request Modal */}
			<CreateSessionRequestModal
				open={openCreateModal}
				handleClose={() => setOpenCreateModal(false)}
			/>
		</CommCard>
	);
};

export default SessionRequestList;
